<!-- @component This component is designed handle inventorysetting fields(inventoryFields in the API) -->
<script lang="ts">
	import type { ComponentProps } from 'svelte'
	import type {
		SearchField,
		NumberRange as NumberRangeType,
		StringRange as StringRangeType,
		CurrencyRange as CurrencyRangeType,
		DateRange as DateRangeType,
		AdditionalFieldValue,
		SearchFieldProps,
	} from 'utility/search-fields'

	import Pin from 'components/search/Pin.svelte'
	import DateRange from 'components/search/DateRange.svelte'
	import Input from '@isoftdata/svelte-input'
	import InputRange from './InputRange.svelte'
	import NumberRange from './NumberRange.svelte'
	import { onMount } from 'svelte'
	import CurrencyRange from 'components/search/CurrencyRange.svelte'
	import EditModeControls from 'components/search/EditModeControls.svelte'
	import Autocomplete from '@isoftdata/svelte-autocomplete'
	import CurrencyInput from '@isoftdata/svelte-currency-input'
	import NullableRadioCheckbox from 'components/NullableRadioCheckbox.svelte'
	import type { i18n } from 'types/common'
	import { getContext } from 'svelte'
	const { t: translate } = getContext<i18n>('i18next')

	interface Props extends SearchFieldProps<AdditionalFieldValue> {
		id?: string
		value?: AdditionalFieldValue
	}

	let {
		inEditMode,
		searchField,
		disabled = false,
		pinnedSearchFields,
		selectedSearchFieldsKeys,
		value = $bindable(getDefaultValue(searchField)),
		id = '',
		change,
	}: Props = $props()

	const label = $derived(searchField.name)
	const disableInput = $derived(disabled || inEditMode)

	function getDefaultValue(searchField: SearchField) {
		if (searchField?.additionalFieldData?.searchByRange) {
			return { from: '', to: '' }
		} else if (searchField?.additionalFieldData?.fieldType === 'BOOLEAN') {
			return true
		} else {
			return ''
		}
	}

	const numberTypes = ['ID', 'INT', 'UINT', 'FLOAT']
	const dateTypes = ['DATE', 'DATETIME']

	const isRange = (field: SearchField) => !!field?.additionalFieldData?.searchByRange

	// Type guards
	function isNumber(field: SearchField, value): value is number | null {
		return !!(
			field?.additionalFieldData?.fieldType &&
			numberTypes.includes(field?.additionalFieldData?.fieldType) &&
			!isRange(field)
		)
	}

	function isNumberRange(field: SearchField, value): value is NumberRangeType {
		return !!(
			field?.additionalFieldData?.fieldType &&
			numberTypes.includes(field?.additionalFieldData?.fieldType) &&
			isRange(field)
		)
	}

	function isString(field: SearchField, value): value is string | null {
		return field?.additionalFieldData?.fieldType === 'TEXT' && !isRange(field)
	}

	function isStringRange(field: SearchField, value): value is StringRangeType {
		return field?.additionalFieldData?.fieldType === 'TEXT' && isRange(field)
	}

	function isCurrency(field: SearchField, value): value is string {
		return field?.additionalFieldData?.fieldType === 'CURRENCY' && !isRange(field)
	}

	function isCurrencyRange(field: SearchField, value): value is CurrencyRangeType {
		return field?.additionalFieldData?.fieldType === 'CURRENCY' && isRange(field)
	}

	function isDate(field: SearchField, value): value is string {
		return !!(
			field?.additionalFieldData?.fieldType &&
			dateTypes.includes(field?.additionalFieldData?.fieldType) &&
			!isRange(field)
		)
	}

	function isDateRange(field: SearchField, value): value is DateRangeType {
		return !!(
			field?.additionalFieldData?.fieldType &&
			dateTypes.includes(field?.additionalFieldData?.fieldType) &&
			isRange(field)
		)
	}

	function isBoolean(field: SearchField, value): value is boolean | null {
		return field?.additionalFieldData?.fieldType === 'BOOLEAN' && !isRange(field)
	}

	// Change event handlers
	function currencyChangeHandler(newValue: string, field: SearchField) {
		change?.({ field, value: newValue })
	}

	function dateRangeHandler(
		ctx: Parameters<Required<ComponentProps<typeof DateRange>>['change']>[0],
		field: SearchField,
	) {
		change?.({ field, value: ctx.dates })
	}

	function genericComponentHandler(value: NumberRangeType | StringRangeType | string | null, field: SearchField) {
		change?.({ field, value: value })
	}

	function elementChangeHandler(e: Event, field: SearchField) {
		const eventValue = (e.target as HTMLInputElement)?.value
		if (field?.additionalFieldData?.fieldType && numberTypes.includes(field?.additionalFieldData?.fieldType)) {
			value = parseFloat(eventValue.replace(/[^\d.]/g, '')) //remove any non-numeric values
		} else {
			value = eventValue
		}
		change?.({ field, value })
	}

	function handleBooleanField(value: AdditionalFieldValue, searchField: SearchField) {
		if (searchField.additionalFieldData?.fieldType === 'BOOLEAN') {
			change?.({ field: searchField, value: value })
		}
	}

	onMount(() => {
		// TODO: remove this after I find where upstream is setting boolean additional fields to true/false instead of null
		if (isBoolean(searchField, value)) {
			value = null
		}
	})
</script>

<EditModeControls
	{searchField}
	{inEditMode}
	{pinnedSearchFields}
	{selectedSearchFieldsKeys}
>
	{#if searchField?.additionalFieldData}
		{#if isNumberRange(searchField, value)}
			<NumberRange
				{id}
				{label}
				from={value.from}
				to={value.to}
				disabled={disableInput}
				change={v => genericComponentHandler(v, searchField)}
				isPinned={pinnedSearchFields.has(searchField.key)}
			/>
		{:else if isStringRange(searchField, value)}
			<InputRange
				{id}
				{label}
				from={value.from}
				to={value.to}
				disabled={disableInput}
				change={v => genericComponentHandler(v, searchField)}
				isPinned={pinnedSearchFields.has(searchField.key)}
			/>
		{:else if isCurrencyRange(searchField, value)}
			<CurrencyRange
				{id}
				{label}
				from={value.from}
				to={value.to}
				disabled={disableInput}
				isPinned={pinnedSearchFields.has(searchField.key)}
				change={v => genericComponentHandler(v, searchField)}
			/>
		{:else if isDateRange(searchField, value)}
			<DateRange
				{id}
				{label}
				dates={value}
				disabled={disableInput}
				isPinned={pinnedSearchFields.has(searchField.key)}
				change={ctx => dateRangeHandler(ctx, searchField)}
			/>
		{:else if isCurrency(searchField, value)}
			<CurrencyInput
				{id}
				{label}
				{value}
				disabled={disableInput}
				change={e => currencyChangeHandler(e.value, searchField)}
			>
				{#snippet hint()}
					<Pin isPinned={pinnedSearchFields.has(searchField.key)} />
				{/snippet}
			</CurrencyInput>
		{:else if isNumber(searchField, value) || isString(searchField, value) || isDate(searchField, value)}
			<Input
				{id}
				{label}
				{value}
				disabled={disableInput}
				type={isDate(searchField, value) ? 'date' : 'text'}
				onchange={e => elementChangeHandler(e, searchField)}
			>
				{#snippet hint()}
					<Pin isPinned={pinnedSearchFields.has(searchField.key)} />
				{/snippet}
			</Input>
		{:else if isBoolean(searchField, value)}
			<NullableRadioCheckbox
				{id}
				{label}
				disabled={disableInput}
				bind:value
				change={newValue => handleBooleanField(newValue, searchField)}
			>
				{#snippet hint()}
					<Pin isPinned={pinnedSearchFields.has(searchField.key)} />
				{/snippet}
			</NullableRadioCheckbox>
		{:else if searchField?.additionalFieldData?.fieldType === 'CHOICE' && searchField?.additionalFieldData?.fieldOptions}
			<Autocomplete
				{id}
				{label}
				{value}
				disabled={disableInput}
				options={searchField.additionalFieldData.fieldOptions}
				emptyValue={null}
				change={option => genericComponentHandler(option, searchField)}
			>
				{#snippet hint()}
					<Pin isPinned={pinnedSearchFields.has(searchField.key)} />
				{/snippet}
			</Autocomplete>
		{:else}
			<!-- TODO: remove this maybe? In theory there could be a combination of fieldType and searchByRange that I'm not accounting for, so maybe they should stay? Or something better? -->
			<p>
				{translate('vehicleSearch.unsupportedFieldType', 'Unsupported field type')}: {searchField.additionalFieldData
					.fieldType}
			</p>
		{/if}
	{/if}
</EditModeControls>
